/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import { TABLE_NAMES, useDateTime } from "../../../../../../../shared";
import useCustomResources from "../../../../../../../shared/hooks/useCustomResources/useCustomResources";
import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "../../../../../../BannerMessage/BannerMessage";
import { ScheduleDto } from "../../../../../types";
import DetailsCardSingleItem from "../../DetailsCardSingleItem/DetailsCardSingleItem";
import { DRIVER_SCOPE } from "../../ScheduleQuickDownload/types";

interface IScheduleDetailsCardContentProps {
  schedule: ScheduleDto;
  resourceName: TABLE_NAMES;
  lastDownloadAttempt?: boolean;
  lastSuccessfulDownload?: boolean;
  currentStatus?: boolean;
}

const ScheduleDetailsCardContent = ({
  schedule,
  resourceName,
  lastDownloadAttempt,
  lastSuccessfulDownload,
  currentStatus
}: IScheduleDetailsCardContentProps) => {
  const { renderLastDownloadAttemptBadge } = useCustomResources();

  const { formattedDate } = useDateTime();
  const { t } = useTranslation();

  const { quick, wasLastAttemptSuccessful } = schedule;

  const bannerData = wasLastAttemptSuccessful
    ? {
        status: BANNER_MESSAGE_STATUSES.Success,
        title: t("Schedules##quick schedule##success banner")
      }
    : {
        status: BANNER_MESSAGE_STATUSES.Info,
        title: t(`Schedules##quick schedule##info banner##${resourceName}`)
      };

  const getDriverScopeStatus = (
    driverCardNum: DRIVER_SCOPE | undefined,
    secondaryScope: DRIVER_SCOPE
  ) => driverCardNum === DRIVER_SCOPE.Both || driverCardNum === secondaryScope;

  return (
    <>
      {quick && (
        <BannerMessage
          data-testid="quick-schedule-message"
          status={bannerData.status}
          title={bannerData.title}
          css={css({ margin: "0 0 16px 0" })}
        />
      )}

      {currentStatus && (
        <DetailsCardSingleItem
          title={t("Details##current status")}
          textToRender={t(`Details##${schedule.currentStatus}`)}
        />
      )}

      {lastDownloadAttempt && (
        <DetailsCardSingleItem
          title={
            quick ? t("Details##status") : t("Details##last download attempt")
          }
        >
          <div css={css({ alignSelf: "baseline" })}>
            {renderLastDownloadAttemptBadge(wasLastAttemptSuccessful)}
          </div>
        </DetailsCardSingleItem>
      )}

      {lastSuccessfulDownload && (
        <DetailsCardSingleItem
          title={
            quick
              ? t("Details##download time")
              : t("Details##last successful download")
          }
          textToRender={
            formattedDate(schedule.lastSuccessfulDownloadTime) ?? "-"
          }
          alignUnset
        />
      )}

      {quick && resourceName === TABLE_NAMES.Vehicles && (
        <>
          {getDriverScopeStatus(schedule?.driverScope, DRIVER_SCOPE.Slot1) && (
            <DetailsCardSingleItem
              title={`${t("Schedules##driver number", { driverNum: 1 })} (ID)`}
              textToRender={schedule.driverCardNumber1 ?? "-"}
              alignUnset
            />
          )}

          {getDriverScopeStatus(schedule?.driverScope, DRIVER_SCOPE.Slot2) && (
            <DetailsCardSingleItem
              title={`${t("Schedules##driver number", { driverNum: 2 })} (ID)`}
              textToRender={schedule.driverCardNumber2 ?? "-"}
              alignUnset
            />
          )}
        </>
      )}
    </>
  );
};

export default ScheduleDetailsCardContent;
